/**
 * Bangladesh land units — single source of truth.
 *
 * All conversions use square feet (sq ft) as the base unit.
 *   input_sqft = amount * sqftPer[from]
 *   result    = input_sqft / sqftPer[to]
 *
 * Authoritative basis (BD Govt / RAJUK):
 *   1 Katha = 720 sq ft
 *   1 Bigha = 20 Katha = 14,400 sq ft
 *   1 Decimal (Shotok / Shotangsho) = 435.6 sq ft = 1/100 Acre
 *   1 Acre   = 43,560 sq ft = 100 Decimal
 *   1 Hectare = 10,000 sq m = 107,639.1 sq ft
 *
 * Regional variations (Chittagong / Noakhali / Sylhet):
 *   1 Kani = 40 Decimal = 17,424 sq ft  (Chittagong)
 *   1 Gonda = 2 Decimal = 871.2 sq ft   (1/20 Kani)
 *   1 Kora  = 0.5 Decimal = 217.8 sq ft (1/4 Gonda)
 *   1 Kranti = 0.125 Decimal = 54.45 sq ft (1/4 Kora)
 *   1 Til    = 1/32 Decimal ≈ 13.6125 sq ft (1/4 Kranti)
 */

export type LandCategory = "standard" | "regional" | "international" | "traditional";

export interface LandUnit {
  id: string;
  en: string;
  bn: string;
  symbol?: string;
  /** Square feet per 1 unit (authoritative). */
  sqft: number;
  category: LandCategory;
  note?: { en: string; bn: string };
}

export const LAND_UNITS: LandUnit[] = [
  // --- Standard / most common in Bangladesh ---
  { id: "sqft",    en: "Square Feet",    bn: "বর্গফুট",       symbol: "sq ft", sqft: 1,        category: "standard" },
  { id: "sqyd",    en: "Square Yard",    bn: "বর্গগজ",        symbol: "sq yd", sqft: 9,        category: "standard" },
  { id: "sqm",     en: "Square Meter",   bn: "বর্গমিটার",     symbol: "m²",    sqft: 10.7639,  category: "standard" },
  { id: "chatak",  en: "Chatak",         bn: "ছটাক",          sqft: 45,                       category: "standard",
    note: { en: "1/16 of a Katha.", bn: "১ কাঠার ১/১৬ ভাগ।" } },
  { id: "decimal", en: "Decimal",        bn: "ডেসিমেল",       sqft: 435.6,                    category: "standard",
    note: { en: "1/100 of an Acre. Same as Shotangsho/Shotok in BD.", bn: "১ একরের ১/১০০। বাংলাদেশে শতাংশ/শতকের সমান।" } },
  { id: "shotok",  en: "Shotok (Shotangsho)", bn: "শতক (শতাংশ)", sqft: 435.6,                category: "standard",
    note: { en: "Identical to Decimal in Bangladesh.", bn: "বাংলাদেশে ডেসিমেলের সমান।" } },
  { id: "katha",   en: "Katha (BD standard)", bn: "কাঠা",     sqft: 720,                      category: "standard",
    note: { en: "Standard BD katha used by RAJUK and most districts. 720 sq ft.", bn: "RAJUK ও বেশিরভাগ জেলায় প্রচলিত মান কাঠা — ৭২০ বর্গফুট।" } },
  { id: "bigha",   en: "Bigha (BD standard)", bn: "বিঘা",     sqft: 14400,                    category: "standard",
    note: { en: "20 Katha = 1 Bigha = 14,400 sq ft.", bn: "২০ কাঠা = ১ বিঘা = ১৪,৪০০ বর্গফুট।" } },
  { id: "acre",    en: "Acre",           bn: "একর",           symbol: "ac",    sqft: 43560,    category: "standard",
    note: { en: "100 Decimal = 1 Acre.", bn: "১০০ ডেসিমেল = ১ একর।" } },
  { id: "hectare", en: "Hectare",        bn: "হেক্টর",        symbol: "ha",    sqft: 107639.1, category: "standard",
    note: { en: "10,000 sq m = 1 Hectare.", bn: "১০,০০০ বর্গমিটার = ১ হেক্টর।" } },

  // --- Regional (Chittagong / Noakhali / Sylhet) ---
  { id: "bigha-sylhet", en: "Bigha (Sylhet/regional)", bn: "বিঘা (সিলেট)", sqft: 12470, category: "regional",
    note: { en: "Some Sylhet & North Bengal mouzas record 1 Bigha ≈ 12,470 sq ft. Always verify against local Khatian.", bn: "কিছু সিলেট ও উত্তরবঙ্গের মৌজায় ১ বিঘা ≈ ১২,৪৭০ বর্গফুট। স্থানীয় খতিয়ান যাচাই করুন।" } },
  { id: "kani",   en: "Kani",   bn: "কানি",   sqft: 17424,    category: "regional",
    note: { en: "Chittagong: 1 Kani = 40 Decimal = 17,424 sq ft.", bn: "চট্টগ্রাম: ১ কানি = ৪০ ডেসিমেল = ১৭,৪২৪ বর্গফুট।" } },
  { id: "gonda",  en: "Gonda",  bn: "গণ্ডা",  sqft: 871.2,    category: "regional",
    note: { en: "1 Gonda = 2 Decimal = 1/20 Kani.", bn: "১ গণ্ডা = ২ ডেসিমেল = ১/২০ কানি।" } },
  { id: "kora",   en: "Kora",   bn: "কড়া",    sqft: 217.8,    category: "regional",
    note: { en: "1 Kora = 0.5 Decimal = 1/4 Gonda.", bn: "১ কড়া = ০.৫ ডেসিমেল = ১/৪ গণ্ডা।" } },
  { id: "kranti", en: "Kranti", bn: "ক্রান্তি", sqft: 54.45,   category: "regional",
    note: { en: "1 Kranti = 1/8 Decimal = 1/4 Kora.", bn: "১ ক্রান্তি = ১/৮ ডেসিমেল = ১/৪ কড়া।" } },
  { id: "til",    en: "Til",    bn: "তিল",    sqft: 13.6125,  category: "regional",
    note: { en: "Smallest customary unit. 1 Til = 1/32 Decimal.", bn: "ক্ষুদ্রতম প্রচলিত একক। ১ তিল = ১/৩২ ডেসিমেল।" } },
  { id: "ojutangsho", en: "Ojutangsho", bn: "অযুতাংশ", sqft: 4.356, category: "regional",
    note: { en: "1/100 of a Decimal (1/10,000 of an Acre).", bn: "১ ডেসিমেলের ১/১০০ (১ একরের ১/১০,০০০)।" } },

  // --- Traditional cubit-based ---
  { id: "hath",      en: "Hath (Cubit, linear→area)", bn: "হাত", sqft: 2.25, category: "traditional",
    note: { en: "1 Hath ≈ 1.5 ft. Square Hath = 1.5 × 1.5 = 2.25 sq ft.", bn: "১ হাত ≈ ১.৫ ফুট। বর্গহাত = ১.৫ × ১.৫ = ২.২৫ বর্গফুট।" } },
  { id: "sqhath",    en: "Square Hath", bn: "বর্গহাত", sqft: 2.25, category: "traditional" },
];

export const UNITS_BY_ID: Record<string, LandUnit> = Object.fromEntries(
  LAND_UNITS.map((u) => [u.id, u]),
);

/** Quick conversion presets (chips) shown under the converter. */
export const QUICK_CHIPS: { from: string; to: string; amount: number }[] = [
  { from: "bigha",   to: "sqft",    amount: 1 },
  { from: "katha",   to: "sqft",    amount: 1 },
  { from: "katha",   to: "decimal", amount: 1 },
  { from: "decimal", to: "sqft",    amount: 1 },
  { from: "acre",    to: "bigha",   amount: 1 },
  { from: "hectare", to: "acre",    amount: 1 },
  { from: "kani",    to: "decimal", amount: 1 },
  { from: "bigha",   to: "decimal", amount: 1 },
];
